/*
 * Setting 723 "Repost Caption Template" (2026-10-12, customer Varun; the owner's decisions D1-D10): the caption every
 * reposted post gets, written by the customer as free text with tokens and spintax. The worker builds the real caption
 * (BC runner/RepostCaptionTemplate.java) and the server checks the text when it is saved (NSB
 * service/RepostCaptionTemplate.check). This file is the page's PREVIEW of both, and it is a PORT, not a look-alike
 * (verification items 1-5, 2026-10-13: what the customer sees must be exactly what the worker posts and what the server
 * says at save time):
 *
 *   - the warnings, hints, notes and counts = a port of the server's check() (scanTokens / warningFor / hint, the brace,
 *     N2 and N3 notes, the E2 longest own text, the blank rule; N2 "There is no <originalcaption>" over EVERY spin -
 *     tokensSomeSpinFills, verification 3 fix B). Pinned by __fixtures__/repostCaption723.serverCheck.json,
 *     which the SERVER writes from its own check() (NSB RepostCaptionTemplateV3FixtureTest) - V3 is compared with it;
 *   - the caption = a port of the worker's prepare() + build(): CRLF / CR -> LF, the <br> forms -> LF, the spin (the
 *     product's innermost-first spintax, nested too), the <br> forms -> LF AGAIN (a spin can build one: 'A<{br|br}>B'),
 *     rule E for an empty original, the one-pass fill, Java's String.strip() of the ends (it keeps U+00A0), the 2,199 cut
 *     of the original only (the worker's binary search and its ONE cut rule, safeBoundary below: UAX #29 extended
 *     grapheme clusters on the EMBEDDED Unicode 16.0 tables, never Intl.Segmenter or the browser's Unicode). Pinned by
 *     __fixtures__/repostCaption723.workerBuild.json, written by the WORKER's own class
 *     (scripts/repost-caption-fixture/WorkerCaptionFixture.java);
 *   - blank = the worker's Java String.isBlank (Character.isWhitespace): U+2003 / tab only is no template; U+00A0 only IS
 *     a template (the worker posts it), and so says the server;
 *   - token case = ASCII only, everywhere (verification fix F1, 2026-10-13): <OriginalCaption> is the token, but
 *     '<or\u0131ginalcaption>' (dotless i) or '<ORIG\u0130NALCAPTION>' (dotted I) is NOT - it is posted as typed, also by
 *     rule E (the worker's ruleE used Java's equalsIgnoreCase, which matched them);
 *   - the Unicode tables (the grapheme classes of the cut, the hashtag letters of the N3 note) are generated ONCE by
 *     scripts/repost-caption-unicode/gen-unicode-723.mjs into the GENERATED region at the end of this file - the same data
 *     strings as in the worker and the server (verification fixes F2, F3).
 *
 * Not in the preview: the hashtags of 646-648 (added after this on the phone) and 653/654/655 (they clean the real
 * original caption first) - the panel says so. Pure, no React; exported for the tests.
 */

export const REPOST_CAPTION_TAG = '[REPOST-CAPTION]';

/** The server's field name (javaField) of setting 723 - design section 3.2. The page draws the preview for it. */
export const REPOST_CAPTION_FIELD = 'repostCaptionTemplate';

/** Instagram allows 2,200; the worker's PostPostingData.getCaption() appends one space at post time. */
export const REPOST_CAPTION_LIMIT = 2199;

/** The most text the server stores for 723 (design section 3.2). */
export const REPOST_CAPTION_MAX_STORED = 10000;

/** Instagram's longest username - what <sourceusername> counts as in the longest-spin length (design section 7). */
export const SOURCE_USERNAME_WEIGHT = 30;

export const VALID_TOKENS = Object.freeze(['<sourceusername>', '<originalcaption>', '<br>']);
const VALID_TOKENS_TEXT = 'The tokens are <sourceusername>, <originalcaption> and <br>.';
const INSTAGRAM_CAPTION_LIMIT = 2200;
const MAX_HASHTAGS = 30;
const MAX_MENTIONS = 20;

const ELLIPSIS = '\u{2026}';
const NUL = /\x00/g;
const GROUP = /\{[^{}]*\}/;                                   // the product's spintax pattern, innermost group
const BR = /<br( ?\/)?>/gi;                                   // <br>, <br/>, <br /> in any case (the worker's (?i)<br(?: ?/)?>)
const FILL = /<(sourceusername|originalcaption)>/gi;

// ------------------------------------------------------------------------------------------------ Java's whitespace
/**
 * Java's Character.isWhitespace (JDK 17), which String.strip / String.isBlank use: tab, LF, VT, FF, CR, U+001C-U+001F,
 * space, U+1680, U+2000-U+2006, U+2008-U+200A, U+2028, U+2029, U+205F, U+3000. NOT U+00A0, U+2007, U+202F (no-break
 * spaces) and not U+FEFF / U+0085 - unlike JS trim(). All are BMP, so a code-unit test is exact.
 */
const JAVA_WS = new Set([0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x1c, 0x1d, 0x1e, 0x1f, 0x20, 0x1680, 0x2000, 0x2001, 0x2002, 0x2003, 0x2004,
  0x2005, 0x2006, 0x2008, 0x2009, 0x200a, 0x2028, 0x2029, 0x205f, 0x3000]);
export const javaIsWhitespace = (code) => JAVA_WS.has(code);

/** Java's String.strip(). */
export function javaStrip(s) {
  const t = String(s);
  let a = 0;
  let b = t.length;
  while (a < b && JAVA_WS.has(t.charCodeAt(a))) a += 1;
  while (b > a && JAVA_WS.has(t.charCodeAt(b - 1))) b -= 1;
  return t.slice(a, b);
}

/** Java's String.stripTrailing(). */
export function javaStripTrailing(s) {
  const t = String(s);
  let b = t.length;
  while (b > 0 && JAVA_WS.has(t.charCodeAt(b - 1))) b -= 1;
  return t.slice(0, b);
}

/** Java's String.isBlank(): empty or only Character.isWhitespace. The worker's "no template" rule, and the server's. */
export function javaIsBlank(s) {
  if (s === null || s === undefined) return true;
  const t = String(s);
  for (let i = 0; i < t.length; i += 1) if (!JAVA_WS.has(t.charCodeAt(i))) return false;
  return true;
}

/** CRLF and lone CR -> LF (the worker's and the server's lf()). */
const lf = (s) => String(s).replace(/\r\n?/g, '\n');
/** The template's own <br> forms -> LF. */
const brToLf = (s) => String(s).replace(BR, '\n');

/** CRLF / CR -> LF, then the template's own <br> forms -> LF (the worker's prepare: its "normalized"). */
export function normaliseTemplate(template) {
  if (template === null || template === undefined) return '';
  return brToLf(lf(template));
}

/** A seeded random in [0, 1) (mulberry32), so a preview spin is repeatable and a test can seed it. */
export function seededRandom(seed) {
  let a = (Number(seed) >>> 0) || 1;
  return function rnd() {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * One spin of the text: innermost group first, a uniform pick per group, then start again - the product's spintax()
 * exactly, with the random passed in. `rnd` is a function giving [0, 1), or an object with nextInt(n) (the
 * java.util.Random contract: the tests drive this with a port of it and get the worker's own outputs byte for byte,
 * __fixtures__/spintax.java-r15h1.json). Never throws; the loop guard is never reached in practice (every round
 * removes at least the two braces).
 */
export function spin(text, rnd = Math.random) {
  let s = text === null || text === undefined ? '' : String(text);
  const pick = rnd && typeof rnd.nextInt === 'function' ? (n) => rnd.nextInt(n) : (n) => Math.floor(rnd() * n);
  for (let guard = 0; guard < 100000; guard += 1) {
    const m = GROUP.exec(s);
    if (!m) return s;
    const options = m[0].slice(1, -1).split('|');            // JS keeps empty options, like split("\\|", -1)
    let i = pick(options.length);
    if (!(i >= 0 && i < options.length)) i = 0;
    s = s.slice(0, m.index) + options[i] + s.slice(m.index + m[0].length);
  }
  return s;
}

/**
 * The template's spin groups, nesting depth and number of possible spins (paths, capped), from the same innermost-first
 * reduction: each innermost group becomes a marker carrying its own count and depth. (The preview's "N versions".)
 */
export function spinShape(text, cap = 1000000) {
  let s = text === null || text === undefined ? '' : String(text).replace(NUL, '');
  const count = [];
  const depth = [];
  const marker = /\x00(\d+)\x00/g;
  for (let guard = 0; guard < 100000; guard += 1) {
    const m = GROUP.exec(s);
    if (!m) break;
    let total = 0;
    let deepest = 0;
    m[0].slice(1, -1).split('|').forEach((option) => {
      let paths = 1;
      option.replace(marker, (x, n) => {
        paths = Math.min(cap, paths * count[Number(n)]);
        deepest = Math.max(deepest, depth[Number(n)]);
        return x;
      });
      total = Math.min(cap, total + paths);
    });
    count.push(total);
    depth.push(deepest + 1);
    s = `${s.slice(0, m.index)}\x00${count.length - 1}\x00${s.slice(m.index + m[0].length)}`;
  }
  let versions = 1;
  let maxDepth = 0;
  s.replace(marker, (x, n) => {
    versions = Math.min(cap, versions * count[Number(n)]);
    maxDepth = Math.max(maxDepth, depth[Number(n)]);
    return x;
  });
  return { groups: count.length, depth: maxDepth, versions, capped: versions >= cap };
}

/** Every distinct spin of the text, in the order the options are written; at most `max` of them. */
export function listSpins(text, max = 50) {
  const out = new Set();
  let leaves = 0;
  const walk = (s) => {
    if (out.size >= max || leaves > 20000) return;
    const m = GROUP.exec(s);
    if (!m) { out.add(s); leaves += 1; return; }
    m[0].slice(1, -1).split('|').forEach((option) => walk(s.slice(0, m.index) + option + s.slice(m.index + m[0].length)));
  };
  walk(text === null || text === undefined ? '' : String(text));
  return Array.from(out);
}

/** The braces spintax leaves as typed - the unbalanced ones - with their place in the text (1-based line/column). */
export function unbalancedBraces(text) {
  const src = text === null || text === undefined ? '' : String(text);
  let s = src;
  for (let guard = 0; guard < 100000; guard += 1) {
    const m = GROUP.exec(s);
    if (!m) break;
    s = s.slice(0, m.index) + '\x01'.repeat(m[0].length) + s.slice(m.index + m[0].length);
  }
  const out = [];
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '{' || s[i] === '}') {
      const before = src.slice(0, i);
      const line = before.split('\n').length;
      const column = i - before.lastIndexOf('\n');
      out.push({ ch: s[i], index: i, line, column });
    }
  }
  return out;
}

// ======================================================================================== E2: the longest own text
// A port of the server's RepostCaptionTemplate.longestOwnText (review item 3, 2026-10-13) - the same states, the same steps.

/** The spans the worker turns into something else, without their closing '>': the <br> forms (a line break) and the two tokens. */
const SPAN_HEADS = ['<br', '<br/', '<br /', '<sourceusername', '<originalcaption'];
const PENDING = [''];
SPAN_HEADS.forEach((h) => {
  for (let k = 1; k <= h.length; k += 1) {
    const s = h.slice(0, k);
    if (!PENDING.includes(s)) PENDING.push(s);
  }
});
const PENDING_NEXT = PENDING.map((p, i) => {
  const row = new Int32Array(128).fill(-1);
  if (i > 0) for (let c = 0; c < 128; c += 1) row[c] = PENDING.indexOf(p + String.fromCharCode(c));
  return row;
});
const PENDING_LT = PENDING.indexOf('<');
const NP = PENDING.length;
const NEG = -1e12;

/** (phase, c, e) of one state: phase 0 = only whitespace posted yet (strip drops it); 1 = c with the trailing whitespace, e without. */
const acc = { phase: 0, c: 0, e: 0 };
function accUnit(ws, weight) {
  if (acc.phase === 0) {
    if (!ws) { acc.phase = 1; acc.c = weight; acc.e = weight; }
    return;
  }
  acc.c += weight;
  if (!ws) acc.e = acc.c;
}
function accRaw(chars) {
  for (let i = 0; i < chars.length; i += 1) accUnit(JAVA_WS.has(chars.charCodeAt(i)), 1);
}

function e2Vec() {
  return [new Float64Array(2 * NP).fill(NEG), new Float64Array(2 * NP).fill(NEG)];
}

function e2Step(inV, code, outV) {
  const [inC, inE] = inV;
  const [outC, outE] = outV;
  outC.fill(NEG);
  outE.fill(NEG);
  for (let s = 0; s < 2 * NP; s += 1) {
    if (inC[s] === NEG) continue;
    acc.phase = s >= NP ? 1 : 0;
    acc.c = inC[s];
    acc.e = inE[s];
    const p = s % NP;
    let np;
    if (code === 0x3c) {                                     // '<'
      accRaw(PENDING[p]);
      np = PENDING_LT;
    } else if (code === 0x3e) {                              // '>'
      const h = PENDING[p];
      if (h === '<br' || h === '<br/' || h === '<br /') accUnit(true, 1);
      else if (h === '<sourceusername') accUnit(false, SOURCE_USERNAME_WEIGHT);
      else if (h === '<originalcaption') accUnit(false, 0);
      else { accRaw(h); accUnit(false, 1); }
      np = 0;
    } else {
      const lc = code >= 0x41 && code <= 0x5a ? code + 32 : code;
      const q = p === 0 || lc >= 128 ? -1 : PENDING_NEXT[p][lc];
      if (q >= 0) np = q;
      else { accRaw(PENDING[p]); accUnit(JAVA_WS.has(code), 1); np = 0; }
    }
    const t = acc.phase * NP + np;
    if (acc.c > outC[t]) outC[t] = acc.c;
    if (acc.e > outE[t]) outE[t] = acc.e;
  }
}

const e2Copy = (v) => [Float64Array.from(v[0]), Float64Array.from(v[1])];
function e2MaxInto(into, v) {
  for (let k = 0; k < 2; k += 1) for (let i = 0; i < v[k].length; i += 1) if (v[k][i] > into[k][i]) into[k][i] = v[k][i];
}

/** Each brace's partner as the worker's innermost-first spintax pairs them (a stack), or -1 (posted as typed). */
function bracePairs(s) {
  const pair = new Int32Array(s.length).fill(-1);
  const stack = [];
  for (let i = 0; i < s.length; i += 1) {
    const ch = s.charCodeAt(i);
    if (ch === 0x7b) stack.push(i);
    else if (ch === 0x7d && stack.length) { const o = stack.pop(); pair[o] = i; pair[i] = o; }
  }
  return pair;
}

/**
 * E2 (owner D5): the longest text of the customer's own the worker can post - the maximum over EVERY spin of the posted
 * caption's length with a non-empty original, less the original: <sourceusername> = 30, <originalcaption> = 0 (but it is
 * text), a line break = 1, the ends stripped as Java's String.strip strips them. EXACT, in one pass (the server's
 * algorithm: the spin groups are a fixed tree; a 36-state automaton reads the pending '<...' of a span that can still
 * become <br> or a token, and whether anything but whitespace was posted yet; each state keeps its largest count). So a
 * token or a <br> that a spin assembles across groups ('{<source|<origin}{username>|alcaption>}' = 30) counts as the worker
 * fills it, and a blank at an end of a spin does not count.
 */
export function longestOwnText(template) {
  const s = normaliseTemplate(template === null || template === undefined ? '' : template);
  const pair = bracePairs(s);
  const frames = [];                                         // { input, best }
  let cur = e2Vec();
  cur[0][0] = 0;
  cur[1][0] = 0;
  let next = e2Vec();
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code === 0x7b && pair[i] >= 0) {
      frames.push({ input: e2Copy(cur), best: e2Vec() });
    } else if (code === 0x7d && pair[i] >= 0) {
      const f = frames.pop();
      e2MaxInto(f.best, cur);
      cur = f.best;
      next = e2Vec();
    } else if (code === 0x7c && frames.length) {
      const f = frames[frames.length - 1];
      e2MaxInto(f.best, cur);
      cur = e2Copy(f.input);
      next = e2Vec();
    } else {
      e2Step(cur, code, next);
      const t = cur;
      cur = next;
      next = t;
    }
  }
  let best = 0;
  for (let st = 0; st < 2 * NP; st += 1) {
    if (cur[0][st] === NEG) continue;
    acc.phase = st >= NP ? 1 : 0;
    acc.c = cur[0][st];
    acc.e = cur[1][st];
    accRaw(PENDING[st % NP]);
    if (acc.phase === 1 && acc.e > best) best = acc.e;
  }
  return best;
}

// ======================================================================================== N2: which tokens does SOME spin fill?
// A port of the server's RepostCaptionTemplate.tokensSomeSpinFills (verification 3 fix B, 2026-10-13) - the same spin tree, the same
// pending-span states as E2 above, each state with the tokens filled so far; a group keeps the union of its options' states.

const FILLS_SOURCE = 1;
const FILLS_ORIGINAL = 2;

function reachStep(inS, code, outS) {
  outS.fill(0);
  for (let st = 0; st < inS.length; st += 1) {
    if (inS[st]) {
      let filled = Math.floor(st / NP);
      const p = st % NP;
      let np;
      if (code === 0x3c) {                                   // '<'
        np = PENDING_LT;
      } else if (code === 0x3e) {                            // '>'
        const h = PENDING[p];
        if (h === '<sourceusername') filled |= FILLS_SOURCE;
        else if (h === '<originalcaption') filled |= FILLS_ORIGINAL;
        np = 0;
      } else {
        const lc = code >= 0x41 && code <= 0x5a ? code + 32 : code;
        const q = p === 0 || lc >= 128 ? -1 : PENDING_NEXT[p][lc];
        np = q >= 0 ? q : 0;
      }
      outS[filled * NP + np] = 1;
    }
  }
}

/**
 * The tokens the worker fills in AT LEAST ONE spin of the template: bit 1 <sourceusername>, bit 2 <originalcaption> (the
 * worker's usesSource / usesOriginal / fill: the spun text holds the token in any ASCII case). Exact over every spin, one pass:
 * '<{origin|ORIGIN}alcaption>', '<orig{\u0131|i}nalcaption>', '{<source|<origin}{username>|alcaption>}' fill it; a
 * Unicode-case look-alike ('<or\u0131ginalcaption>') never does.
 */
export function tokensSomeSpinFills(template) {
  const s = normaliseTemplate(template === null || template === undefined ? '' : template);
  const pair = bracePairs(s);
  const states = 4 * NP;
  const frames = [];                                         // { input, union }
  let cur = new Uint8Array(states);
  cur[0] = 1;
  let next = new Uint8Array(states);
  const orInto = (into, v) => { for (let i = 0; i < v.length; i += 1) into[i] |= v[i]; };
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code === 0x7b && pair[i] >= 0) {
      frames.push({ input: Uint8Array.from(cur), union: new Uint8Array(states) });
    } else if (code === 0x7d && pair[i] >= 0) {
      const f = frames.pop();
      orInto(f.union, cur);
      cur = f.union;
      next = new Uint8Array(states);
    } else if (code === 0x7c && frames.length) {
      const f = frames[frames.length - 1];
      orInto(f.union, cur);
      cur = Uint8Array.from(f.input);
      next = new Uint8Array(states);
    } else {
      reachStep(cur, code, next);
      const t = cur;
      cur = next;
      next = t;
    }
  }
  let out = 0;
  for (let st = 0; st < states; st += 1) if (cur[st]) out |= Math.floor(st / NP);
  return out;
}

// ======================================================================================== the server's check(), ported
const TOKEN_SOURCE = '<sourceusername>';
const TOKEN_ORIGINAL = '<originalcaption>';
const TOKEN_BR = '<br>';
const TOKEN_FORMS = new Set([TOKEN_SOURCE, TOKEN_ORIGINAL, TOKEN_BR, '<br/>', '<br />']);
const TOKENISH = /<[ \t]*([A-Za-z][A-Za-z0-9_]*)[ \t]*\/?[ \t]*>/g;
const SMM_USERNAME = /<<[ \t]*username[ \t]*>>/gi;
const MENTION = /@(?:[A-Za-z0-9._]|<sourceusername>)/gi;

const n = (v) => Number(v).toLocaleString('en-US');
const countChar = (s, ch) => { let k = 0; for (let i = 0; i < s.length; i += 1) if (s[i] === ch) k += 1; return k; };
const countMatches = (re, s) => { re.lastIndex = 0; let k = 0; while (re.exec(s) !== null) k += 1; return k; };

/**
 * The server's countHashtags (N3, verification fix F3): every '#' followed by a letter or a number (General_Category L / N
 * of the EMBEDDED Unicode 16.0 table, hashtagChar) or '_' - what the old '#[\p{L}\p{N}_]' counted, but on one fixed Unicode
 * version (Java 17's \p{L} is Unicode 13, a browser's its own: '#\u0870' was 0 on the server and 1 here).
 */
export function countHashtags(s) {
  const t = String(s);
  let k = 0;
  for (let i = 0; i + 1 < t.length; i += 1) {
    if (t.charCodeAt(i) === 0x23) {
      const cp = t.codePointAt(i + 1);
      if (cp === 0x5f || hashtagChar(cp)) k += 1;
    }
  }
  return k;
}

/** ASCII-only lower case (A-Z -> a-z, nothing else): the token rule of all three parts (F1). */
export const asciiLower = (s) => String(s).replace(/[A-Z]+/g, (x) => x.toLowerCase());

/** The customer's text as the N3 counts read it: LF, the <br> forms LF, Java's String.strip of the ends (the server's core()). */
const core = (s) => javaStrip(brToLf(lf(s === null || s === undefined ? '' : s)));

/** The server's resolve(): every group innermost-first, keeping the option with the largest score (the first on a tie). */
function resolve(text, score) {
  let s = text;
  for (let guard = 0; ; guard += 1) {
    const m = GROUP.exec(s);
    if (!m) return s;
    if (guard >= 100000) throw new Error('more than 100000 spin groups');
    const options = m[0].slice(1, -1).split('|');
    let best = options[0];
    let bestScore = score(best);
    for (let i = 1; i < options.length; i += 1) {
      const sc = score(options[i]);
      if (sc > bestScore) { best = options[i]; bestScore = sc; }
    }
    s = s.slice(0, m.index) + best + s.slice(m.index + m[0].length);
  }
}

/** The server's where(): "line L, column C" (1-based). */
function where(text, at) {
  let line = 1;
  let col = 1;
  for (let i = 0; i < at; i += 1) {
    if (text[i] === '\n') { line += 1; col = 1; } else col += 1;
  }
  return `line ${line}, column ${col}`;
}

/** The server's scanBraces(): spin groups, depth (paired braces only), the unbalanced ones as "'{' at line L, column C" (5 shown). */
function scanBraces(text, c) {
  const stack = [];
  const unmatchedClose = [];
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '{') {
      stack.push(i);
      c.spinDepth = Math.max(c.spinDepth, stack.length);
    } else if (ch === '}') {
      if (!stack.length) unmatchedClose.push(i);
      else { stack.pop(); c.spinGroups += 1; }
    }
  }
  if (stack.length || unmatchedClose.length) {
    const bad = Array.from(new Set([...stack, ...unmatchedClose])).sort((a, b) => a - b);
    const badSet = new Set(bad);
    let depth = 0;
    let max = 0;
    for (let i = 0; i < text.length; i += 1) {
      if (!badSet.has(i)) {
        if (text[i] === '{') { depth += 1; max = Math.max(max, depth); } else if (text[i] === '}') depth -= 1;
      }
    }
    c.spinDepth = max;
    let shown = 0;
    for (const at of bad) {
      shown += 1;
      if (shown > 5) { c.unbalanced.push(`and ${bad.length - 5} more`); break; }
      c.unbalanced.push(`'${text[at]}' at ${where(text, at)}`);
    }
  }
}

/** Levenshtein distance (small words only; 99 when the lengths differ by more than 3 or a word is over 64) - the server's. */
function distance(a, b) {
  if (Math.abs(a.length - b.length) > 3 || a.length > 64 || b.length > 64) return 99;
  let prev = Array.from({ length: b.length + 1 }, (x, j) => j);
  let cur = new Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i += 1) {
    cur[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(Math.min(cur[j - 1] + 1, prev[j] + 1), prev[j - 1] + cost);
    }
    const t = prev;
    prev = cur;
    cur = t;
  }
  return prev[b.length];
}

const HINT_SOURCE = new Set(['sourceusername', 'source', 'sourceuser', 'sourcename', 'sourceaccount', 'sourcehandle', 'sourceuname', 'owner',
  'postowner', 'ownerusername', 'originalusername', 'originalposter', 'credit']);
const HINT_ORIGINAL = new Set(['originalcaption', 'caption', 'original', 'origcaption', 'origcap', 'originaltext', 'captionoriginal',
  'postcaption']);
const HINT_BR = new Set(['nl', 'newline', 'linebreak', 'lb', 'break', 'enter', 'brk']);

/** The server's hint(bare): the token a misspelt word (lowercase, '_' removed) most likely meant, or null. */
function hint(bare) {
  if (HINT_SOURCE.has(bare)) return TOKEN_SOURCE;
  if (HINT_ORIGINAL.has(bare)) return TOKEN_ORIGINAL;
  if (HINT_BR.has(bare)) return TOKEN_BR;
  if (distance(bare, 'sourceusername') <= 3) return TOKEN_SOURCE;
  if (distance(bare, 'originalcaption') <= 3) return TOKEN_ORIGINAL;
  return null;
}

/** The server's warningFor(typed, name): the note for a token-like word that is not an exact token form, or null. */
function warningFor(typed, name) {
  const lower = name.toLowerCase();
  const bare = lower.replace(/_/g, '');
  const lowercaseWord = name === lower;
  if (lower === 'sourceusername' || lower === 'originalcaption' || lower === 'br') {
    const want = lower === 'br' ? TOKEN_BR : `<${lower}>`;
    return { kind: 'exact', hint: want, message: `'${typed}' is posted exactly as typed: write the token as ${want}, with nothing else inside the angle brackets.` };
  }
  switch (lower) {
    case 'username':
      return {
        kind: 'dm-username',
        hint: TOKEN_SOURCE,
        message: `'${typed}' is a welcome-DM token and is posted exactly as typed here. For the account the post was taken`
          + ' from write <sourceusername> (type @<sourceusername> for a mention).',
      };
    case 'ourname':
    case 'fullname':
    case 'firstname':
    case 'lastname':
      return { kind: 'dm-only', hint: '', message: `'${typed}' works in DMs only and is posted exactly as typed here. ${VALID_TOKENS_TEXT}` };
    default:
      break;
  }
  const h = hint(bare);
  if (h === null && !lowercaseWord) return null;
  return {
    kind: h === null ? 'none' : 'hint',
    hint: h || '',
    message: `'${typed}' is not a caption token, so it is posted exactly as typed.${h === null ? '' : ` Did you mean ${h}?`} ${VALID_TOKENS_TEXT}`,
  };
}

/** The server's doubledWarning(): a real token inside a second pair of angle brackets - the inner token filled, the outer '<' '>' posted. */
function doubledWarning(doubled, inner, lower) {
  if (lower === TOKEN_SOURCE) {
    return `'${doubled}' is posted as the account name inside angle brackets, e.g. <13_beard_86>: the ${inner}`
      + ' inside is filled and the outer angle brackets are posted as typed. Write <sourceusername> with one pair of angle'
      + ' brackets (type @<sourceusername> for a mention).';
  }
  if (lower === TOKEN_ORIGINAL) {
    return `'${doubled}' is posted as the original caption inside angle brackets: the ${inner} inside is filled and`
      + ' the outer angle brackets are posted as typed. Write <originalcaption> with one pair of angle brackets.';
  }
  return `'${doubled}' is posted as '<', a line break and '>': the ${inner} inside becomes the line break and the`
    + ' outer angle brackets are posted as typed. For a line break write <br> with one pair of angle brackets.';
}

/** The server's scanTokens(): counts the tokens; the warnings in order (SMM first, then left to right), each typed form once. */
function scanTokens(text, c) {
  const warnings = new Map();                                // typed -> { message, kind, hint, doubled }
  const seen = new Map();
  const bump = (k) => { const v = (seen.get(k) || 0) + 1; seen.set(k, v); return v; };
  const smm = new Uint8Array(text.length + 1);
  SMM_USERNAME.lastIndex = 0;
  let d;
  while ((d = SMM_USERNAME.exec(text)) !== null) {
    for (let i = d.index; i < d.index + d[0].length; i += 1) smm[i] = 1;
    const typed = d[0];
    if (bump(typed) === 1) {
      c.unknownTokens.push(typed);
      warnings.set(typed, {
        kind: 'smm',
        hint: `@${TOKEN_SOURCE}`,
        message: `'${typed}' is the SMM disclaimer's token and is posted exactly as typed here. In this`
          + ' template write @<sourceusername> (this template does not add the @ for you).',
      });
    }
  }
  TOKENISH.lastIndex = 0;
  let m;
  while ((m = TOKENISH.exec(text)) !== null) {
    if (smm[m.index]) continue;
    const typed = m[0];
    const lower = typed.toLowerCase();
    const end = m.index + typed.length;
    if (TOKEN_FORMS.has(lower)) {
      if (lower === TOKEN_SOURCE) c.sourceTokens += 1;
      else if (lower === TOKEN_ORIGINAL) c.originalTokens += 1;
      else c.brTokens += 1;
      if (m.index > 0 && text[m.index - 1] === '<' && end < text.length && text[end] === '>') {
        const doubled = text.slice(m.index - 1, end + 1);
        if (bump(doubled) === 1) {
          warnings.set(doubled, { kind: 'doubled', hint: lower === TOKEN_SOURCE || lower === TOKEN_ORIGINAL ? lower : TOKEN_BR, message: doubledWarning(doubled, typed, lower), doubled: true });
        }
      }
      continue;
    }
    const w = warningFor(typed, m[1]);
    if (w === null) continue;
    if (bump(typed) === 1) {
      c.unknownTokens.push(typed);
      warnings.set(typed, w);
    }
  }
  seen.forEach((count, key) => {
    if (count > 1 && warnings.has(key)) {
      const w = warnings.get(key);
      warnings.set(key, { ...w, message: `${w.message} (It is there ${count} times.)` });
    }
  });
  return warnings;
}

/**
 * The server's save check of a 723 text (RepostCaptionTemplate.check), ported line for line: errors refuse the save
 * (E2 over 2,199, E3 over 10,000), notes are the warnings shown after it (the token warnings, the unbalanced braces, N2 no
 * <originalcaption>, N3 hashtags / mentions). The same field names as the server's Check. Never throws.
 */
export function serverCheck(raw) {
  const text = lf(raw === null || raw === undefined ? '' : raw);
  const c = {
    text, errors: [], notes: [], noteKinds: [], unknownTokens: [], unbalanced: [], warnings: [], sourceTokens: 0, originalTokens: 0,
    brTokens: 0, lines: 0, spinGroups: 0, spinDepth: 0, longestSpin: 0, maxHashtags: 0, maxMentions: 0, internalError: null,
    someSpinFillsSource: false, someSpinFillsOriginal: false,
  };
  const note = (kind, t) => { c.notes.push(t); c.noteKinds.push(kind); };
  try {
    if (text.length > REPOST_CAPTION_MAX_STORED) {
      c.errors.push(`it is ${n(text.length)} characters long; the most is ${n(REPOST_CAPTION_MAX_STORED)}.`);
      return c;
    }
    scanBraces(text, c);
    const warnings = scanTokens(text, c);
    const own = core(text);
    c.lines = text === '' ? 0 : countChar(brToLf(text), '\n') + 1;
    c.longestSpin = longestOwnText(text);
    const fills = tokensSomeSpinFills(text);                 // N2 over every spin (verification 3 fix B)
    c.someSpinFillsSource = (fills & FILLS_SOURCE) !== 0;
    c.someSpinFillsOriginal = (fills & FILLS_ORIGINAL) !== 0;
    c.maxHashtags = own.indexOf('#') < 0 ? 0 : countHashtags(resolve(own, countHashtags));
    c.maxMentions = own.indexOf('@') < 0 ? 0 : countMatches(MENTION, resolve(own, (s) => countMatches(MENTION, s)));
    if (c.longestSpin > REPOST_CAPTION_LIMIT) {
      c.errors.push(`your own text can reach ${n(c.longestSpin)} characters (its longest spin, counting ${SOURCE_USERNAME_WEIGHT}`
        + ` for each <sourceusername>), and Instagram allows ${n(INSTAGRAM_CAPTION_LIMIT)} in all. Shorten it to at most`
        + ` ${n(REPOST_CAPTION_LIMIT)} (only the original caption is ever shortened to fit, never your own text).`);
    }
    warnings.forEach((w, typed) => { note('warning', w.message); c.warnings.push({ token: typed, ...w }); });
    if (c.unbalanced.length) {
      const one = c.unbalanced.length === 1;
      note('warning', `${c.unbalanced.join(', ')}${one ? ' has' : ' have'} no matching brace, so ${one ? 'it is' : 'they are'}`
        + ` posted as you typed ${one ? 'it' : 'them'} (not spun).`);
    }
    if (!javaIsBlank(text) && !c.someSpinFillsOriginal) {
      note('info', "There is no <originalcaption>, so the original post's caption is not posted - only your text (and"
        + ' the hashtags of 646-648).');
    }
    if (c.maxHashtags > MAX_HASHTAGS) {
      note('warning', `Its longest spin has ${c.maxHashtags} hashtags; Instagram may not accept more than ${MAX_HASHTAGS}`
        + ' (the hashtags of the original caption and of 646-648 come on top).');
    }
    if (c.maxMentions > MAX_MENTIONS) {
      note('warning', `Its longest spin has ${c.maxMentions} @mentions; Instagram may not accept more than ${MAX_MENTIONS}.`);
    }
  } catch (e) {
    c.internalError = String((e && e.message) || e);
  }
  return c;
}

/** The unknown tokens (the server's definition, in its order) with their counts, warning kind, hint and the server's message. */
export function unknownTokens(template) {
  const c = serverCheck(template);
  return c.warnings.filter((w) => !w.doubled).map((w) => ({
    token: w.token, kind: w.kind, hint: w.hint, message: w.message,
  }));
}

/** The message the page shows for one unknown token: the server's own note. */
export function unknownTokenMessage(u) {
  return u.message;
}

/** The whole template, checked: the server's check (the page's live notes = what saving will say) + the preview's extras. */
export function checkRepostCaptionTemplate(template) {
  const raw = template === null || template === undefined ? '' : String(template);
  const blank = javaIsBlank(raw);
  const server = serverCheck(raw);
  const shape = spinShape(normaliseTemplate(raw));
  return {
    blank,
    length: server.text.length,
    lines: server.lines,
    tokens: { sourceusername: server.sourceTokens, originalcaption: server.originalTokens, br: server.brTokens },
    unknown: unknownTokens(raw),
    unbalanced: server.unbalanced,
    groups: server.spinGroups,
    depth: server.spinDepth,
    versions: shape.versions,
    versionsCapped: shape.capped,
    longest: server.longestSpin,
    overLimit: server.longestSpin > REPOST_CAPTION_LIMIT,
    tooLong: server.text.length > REPOST_CAPTION_MAX_STORED,
    errors: server.errors,
    notes: server.notes,
    noteKinds: server.noteKinds,
    server,
  };
}

/** The page's notes: the server's errors (saving refuses the text) and notes (saved, with a warning), its words exactly. */
export function templateNotes(check) {
  if (!check || check.blank) return [];
  const out = check.errors.map((e) => ({ kind: 'error', text: `723 is not saved like this: ${e}` }));
  check.notes.forEach((t, i) => out.push({ kind: check.noteKinds[i] || 'warning', text: t }));
  return out;
}

// ======================================================================================== the worker's caption, ported
/**
 * The source account as the preview uses it: Java's strip, leading '@' dropped (a folder named @x, the worker's
 * folderSourceName); '' when missing (null, blank, or the 4-letter "null" - the worker's isMissingSource).
 */
export function cleanSourceUsername(v) {
  if (v === null || v === undefined) return '';
  let s = javaStrip(String(v));
  while (s.startsWith('@')) s = javaStrip(s.slice(1));
  return s === 'null' ? '' : s;
}

/**
 * The worker's ruleE (D4, an EMPTY original): every line that holds only <originalcaption> (Java strip, any ASCII case -
 * '<or\u0131ginalcaption>' is not the token, F1) goes, with ONE neighbouring blank line (Java isBlank) - the one after it if
 * there is one, else the one before it.
 */
export function dropOriginalLines(text) {
  const lines = String(text).split('\n');
  let i = 0;
  while (i < lines.length) {
    if (asciiLower(javaStrip(lines[i])) === TOKEN_ORIGINAL) {
      lines.splice(i, 1);
      if (i < lines.length && javaIsBlank(lines[i])) {
        lines.splice(i, 1);
      } else if (i - 1 >= 0 && javaIsBlank(lines[i - 1])) {
        lines.splice(i - 1, 1);
        i -= 1;
      }
    } else {
      i += 1;
    }
  }
  return lines.join('\n');
}

/** The worker's fill: both tokens in one pass; a replacer function, so '$' and '\' in a value are plain text. */
export function fillTokens(text, sourceUsername, originalCaption) {
  return String(text).replace(FILL, (x, name) => (name.toLowerCase() === 'sourceusername' ? sourceUsername : originalCaption));
}

// ======================================================================================== the 2,199 cut: ONE rule
// Verification fix F2 (2026-10-13). The worker (BC runner/RepostCaptionTemplate.java safeBoundary / graphemeBreak) and this
// preview run the SAME steps on the SAME embedded Unicode 16.0 tables (the GENERATED region at the end of this file): the
// extended grapheme clusters of UAX #29, rules GB3-GB13 with the Indic conjunct rule GB9c (InCB) and SpacingMark (GB9a: Thai /
// Lao SARA AM). Before: the worker used JDK 17's BreakIterator (Unicode 13, no GB9c, Thai 'tham' split) and V3 Intl.Segmenter
// (the browser's ICU) - they differed for Hindi conjuncts, Thai / Lao AM, Bengali, Gujarati, Telugu, Arabic U+0600.
// Verification 3 fix A (2026-10-13): UAX #29 16.0 leaves a boundary between a virama / invisible stacker and the consonant it
// stacks in every script but the six InCB Linker ones (Kannada, Tamil, Gurmukhi, Sinhala, Khmer, Myanmar ...), so the cut also
// skips a boundary right after one when a letter follows (cutClass: the generated CUT table) - the worker's same step.

const isExtendClass = (k) => k === GC.EXTEND || k === GC.EXTEND_INCB_EXTEND || k === GC.EXTEND_INCB_LINKER;

/**
 * UAX #29: is there an extended grapheme cluster boundary between a code point of class `prev` and the next one, of class
 * `cur`? `emoji` is 2 right after Extended_Pictographic Extend* ZWJ (GB11); `incb` is 2 right after InCB Consonant
 * [Extend | Linker]* Linker [Extend | Linker]* (GB9c); `riRun` = the regional indicators right before (GB12 / GB13).
 */
export function graphemeBreak(prev, cur, emoji, incb, riRun) {
  if (prev === GC.CR && cur === GC.LF) return false;                                             // GB3
  if (prev === GC.CR || prev === GC.LF || prev === GC.CONTROL) return true;                      // GB4
  if (cur === GC.CR || cur === GC.LF || cur === GC.CONTROL) return true;                         // GB5
  if (prev === GC.L && (cur === GC.L || cur === GC.V || cur === GC.LV || cur === GC.LVT)) return false; // GB6
  if ((prev === GC.LV || prev === GC.V) && (cur === GC.V || cur === GC.T)) return false;         // GB7
  if ((prev === GC.LVT || prev === GC.T) && cur === GC.T) return false;                          // GB8
  if (isExtendClass(cur) || cur === GC.ZWJ) return false;                                        // GB9
  if (cur === GC.SPACING_MARK) return false;                                                     // GB9a
  if (prev === GC.PREPEND) return false;                                                         // GB9b
  if (cur === GC.INCB_CONSONANT && incb === 2) return false;                                     // GB9c
  if (cur === GC.EXTENDED_PICTOGRAPHIC && emoji === 2) return false;                             // GB11
  if (cur === GC.REGIONAL_INDICATOR && riRun % 2 === 1) return false;                            // GB12, GB13
  return true;                                                                                   // GB999
}

/**
 * The extended grapheme cluster boundaries of s (UAX #29, graphemeBreak, left to right), ascending: 0, ..., s.length (for "" just
 * 0). The worker's graphemeBoundaries, step for step.
 */
export function graphemeBoundaries(text) {
  const s = String(text);
  const out = [0];
  let prev = -1;
  let emoji = 0;
  let incb = 0;
  let riRun = 0;
  for (let i = 0; i < s.length;) {
    const cp = s.codePointAt(i);
    const k = graphemeClass(cp);
    if (i > 0 && graphemeBreak(prev, k, emoji, incb, riRun)) out.push(i);
    if (k === GC.EXTENDED_PICTOGRAPHIC) emoji = 1;
    else if (emoji === 1 && k === GC.ZWJ) emoji = 2;
    else if (!(emoji === 1 && isExtendClass(k))) emoji = 0;                 // Extend after ExtPict keeps 1
    if (k === GC.INCB_CONSONANT) incb = 1;
    else if (incb >= 1 && k === GC.EXTEND_INCB_LINKER) incb = 2;
    else if (!(incb >= 1 && (k === GC.EXTEND_INCB_EXTEND || k === GC.ZWJ))) incb = 0; // InCB Extend (ZWJ too) keeps it
    riRun = k === GC.REGIONAL_INDICATOR ? riRun + 1 : 0;
    prev = k;
    i += cp > 0xffff ? 2 : 1;
  }
  if (s.length > 0) out.push(s.length);
  return out;
}

/** The code point that ends at offset i (a pair counts as one; a lone surrogate as itself) - Java's String.codePointBefore. */
function codePointBefore(s, i) {
  const lo = s.charCodeAt(i - 1);
  if (lo >= 0xdc00 && lo <= 0xdfff && i >= 2) {
    const hi = s.charCodeAt(i - 2);
    if (hi >= 0xd800 && hi <= 0xdbff) return (hi - 0xd800) * 0x400 + (lo - 0xdc00) + 0x10000;
  }
  return lo;
}

/**
 * THE cut rule of the 2,199 limit - the worker's safeBoundary, step for step: the largest offset b <= max such that
 *   1. b is an extended grapheme cluster boundary of s (graphemeBoundaries: UAX #29 on the embedded Unicode 16.0 tables), and
 *   2. b does not split a conjunct (verification 3 fix A, 2026-10-13): NOT [the code point before b is a virama or invisible
 *      stacker (Indic_Syllabic_Category Virama / Invisible_Stacker: cutClass CUT.VIRAMA) AND the code point at b is a letter
 *      (General_Category L: CUT.LETTER)]. UAX #29 16.0 joins virama + consonant only in the six InCB Linker scripts (GB9c);
 *      in Kannada, Tamil, Gurmukhi, Sinhala, Khmer (coeng), Myanmar (U+1039) ... a cluster boundary falls between them. A
 *      virama before a space, punctuation, a digit or a line break (a word-final Tamil pulli) is kept, and
 *   3. the text before b without its trailing whitespace (Java's isWhitespace - cutOriginal strips it) is not empty and does
 *      not end with U+200D ZWJ (the worker's old rule, kept) or a Prepend character (U+0600 ...: the '\u2026' after it would
 *      join it) - so every cluster kept is whole and the '\u2026' starts a cluster of its own.
 * 0 when there is none; s.length when max >= s.length (nothing is cut).
 */
export function safeBoundary(text, max) {
  const s = String(text);
  if (!(max > 0)) return 0;
  if (max >= s.length) return s.length;
  const bounds = graphemeBoundaries(s);
  for (let j = bounds.length - 1; j > 0; j -= 1) {
    const b = bounds[j];
    // 2. (b < s.length here: b <= max < s.length; b > 0: j > 0)
    if (b <= max && !(cutClass(codePointBefore(s, b)) === CUT.VIRAMA && cutClass(s.codePointAt(b)) === CUT.LETTER)) {
      let e = b;
      while (e > 0 && JAVA_WS.has(s.charCodeAt(e - 1))) e -= 1;
      if (e === 0) return 0;                                 // only whitespace before b (and so before every earlier b)
      const last = graphemeClass(codePointBefore(s, e));
      if (last !== GC.ZWJ && last !== GC.PREPEND) return b;
    }
  }
  return 0;
}

/** The worker's cutOriginal: at most n units - a grapheme-safe prefix of n-1, trailing whitespace dropped, + the ellipsis; null when nothing is left. */
function cutOriginal(orig, units) {
  const end = safeBoundary(orig, units - 1);
  const prefix = javaStripTrailing(orig.slice(0, end));
  if (!prefix) return null;
  return prefix + ELLIPSIS;
}

/** At most `maxUnits` UTF-16 units of `text`, never splitting a grapheme cluster: text.slice(0, safeBoundary(text, maxUnits)). */
export function cutAtGrapheme(text, maxUnits) {
  const s = String(text);
  return s.slice(0, safeBoundary(s, maxUnits));
}

/**
 * One post's caption from the template - the worker's prepare() + build(), step by step (BC runner/RepostCaptionTemplate):
 * no template when it is blank (Java isBlank); CRLF -> LF, <br> -> LF, spin, <br> -> LF again; the source missing and the
 * spin uses <sourceusername>: not queued; the original Java-stripped; empty: rule E, fill, strip; else fill, strip, and over
 * the limit the worker's binary search for the longest grapheme-safe cut of the original (each copy the same), else the
 * original left out. `hashtags` (646-648) are appended as the worker does (the preview passes none). Never throws.
 * @returns { active, caption, spun, missingSource, emptyOriginal, shortened, droppedOriginal, overLimit, length }
 *   active false = blank template (today's caption rules); caption null when missingSource (the post is not queued).
 */
export function buildRepostCaption(template, {
  sourceUsername, originalCaption, rnd = Math.random, limit = REPOST_CAPTION_LIMIT, hashtags = '',
} = {}) {
  const none = { active: false, caption: null, spun: '', missingSource: false, emptyOriginal: false, shortened: false, droppedOriginal: false, overLimit: false, length: 0 };
  try {
    if (template === null || template === undefined || javaIsBlank(String(template))) return none;
    const spun = brToLf(spin(normaliseTemplate(template), rnd));
    const source = cleanSourceUsername(sourceUsername);
    const tags = hashtags === null || hashtags === undefined ? '' : String(hashtags);
    const result = { ...none, active: true, spun };
    if (asciiLower(spun).includes(TOKEN_SOURCE) && !source) return { ...result, missingSource: true };
    const orig = javaStrip(originalCaption === null || originalCaption === undefined ? '' : String(originalCaption));
    const withoutOriginal = javaStrip(fillTokens(dropOriginalLines(spun), source, ''));
    let body;
    let emptyOriginal = false;
    let shortened = false;
    let droppedOriginal = false;
    if (!orig) {
      emptyOriginal = true;
      body = withoutOriginal;
    } else {
      const full = javaStrip(fillTokens(spun, source, orig));
      if (full.length + tags.length <= limit) {
        body = full;
      } else {
        let lo = 2;
        let hi = orig.length;
        let bestBody = null;
        while (lo <= hi) {
          const mid = (lo + hi) >>> 1;
          const cut = cutOriginal(orig, mid);
          if (cut === null) { lo = mid + 1; continue; }
          const b = javaStrip(fillTokens(spun, source, cut));
          if (b.length + tags.length <= limit) { bestBody = b; lo = mid + 1; } else hi = mid - 1;
        }
        if (bestBody !== null) { body = bestBody; shortened = true; } else { body = withoutOriginal; droppedOriginal = true; }
      }
    }
    const caption = body + tags;
    return { ...result, caption, emptyOriginal, shortened, droppedOriginal, overLimit: caption.length > limit, length: caption.length };
  } catch (e) {
    return { ...none, active: true, error: String((e && e.message) || e) };
  }
}

/** One log line describing a template (no customer text beyond 120 characters, line breaks as \n). */
export function describeTemplate(template) {
  const c = checkRepostCaptionTemplate(template);
  if (c.blank) return 'blank (reposted posts keep the caption settings 631/632, 652, 665)';
  const short = String(template).replace(/\r\n?/g, '\n');
  const shown = (short.length > 120 ? `${short.slice(0, 120)}...` : short).replace(/\n/g, '\\n');
  return `${c.length} chars (${c.lines} lines, ${c.groups} spin groups, depth ${c.depth}, ${c.versionsCapped ? 'over a million' : c.versions} spins; `
    + `tokens sourceusername x${c.tokens.sourceusername}, originalcaption x${c.tokens.originalcaption}, br x${c.tokens.br}; `
    + `unknown ${c.unknown.length ? c.unknown.map((u) => u.token).join(' ') : 'none'}; `
    + `unbalanced ${c.unbalanced.length ? c.unbalanced.join(', ') : 'none'}; `
    + `longest own text ${c.longest}) "${shown}"`;
}

// >>> GENERATED unicode-723 (gen-unicode-723.mjs: Unicode 16.0 from Node 22.20.0 / ICU 77.1 - do not edit by hand)
/* The Unicode 16.0 tables setting 723 uses instead of the browser's own: the grapheme classes of the 2,199 cut and the
 * hashtag characters (General_Category L or N). The SAME data strings are in the worker and the server (SHA-256 below).
 * Runs of '<start hex><class letter>' (class = letter - 'A'), each to the next start.
 * The cut classes (the worker and V3): CUT.LETTER = General_Category L, CUT.VIRAMA = Indic_Syllabic_Category Virama or
 * Invisible_Stacker (the list in gen-unicode-723.mjs), CUT.OTHER = the rest. */
export const UNICODE_723_VERSION = '16.0';
export const GRAPHEME_SHA256 = 'c2c0a2e437e9ac9f56bc859d896002fa5089508ecfedb9ec3bd1ff05133811ad';
export const HASHTAG_SHA256 = 'd5c6b6d15cd7d464980e79c2052fc29a81ec54cef17830da8e7d13d428a9bd45';
export const CUT_SHA256 = '0bcf41ee22b147d9973427b263f32adbe6d635feb2c8ae690d7108ba54e9d7b4';
export const GC = Object.freeze({
  OTHER: 0,
  CR: 1,
  LF: 2,
  CONTROL: 3,
  EXTEND: 4,
  EXTEND_INCB_EXTEND: 5,
  EXTEND_INCB_LINKER: 6,
  ZWJ: 7,
  REGIONAL_INDICATOR: 8,
  PREPEND: 9,
  SPACING_MARK: 10,
  L: 11,
  V: 12,
  T: 13,
  LV: 14,
  LVT: 15,
  EXTENDED_PICTOGRAPHIC: 16,
  INCB_CONSONANT: 17,
});
export const CUT = Object.freeze({
  OTHER: 0,
  LETTER: 1,
  VIRAMA: 2,
});
export const GRAPHEME_DATA = ''
  + '0DaCbDdBeD20A7fDa0Aa9QaaAadDaeQafA300F370A483F48aA591F5beA5bfF5c0A5c1F5c3A5c4F5c6A5c7F5c8A600J606A610F61bA61cD61dA64bF660A670F671A6d6F6ddJ6d'
  + 'eA6dfF6e5A6e7F6e9A6eaF6eeA70fJ710A711F712A730F74bA7a6F7b1A7ebF7f4A7fdF7feA816F81aA81bF824A825F828A829F82eA859F85cA890J892A897F8a0A8caF8e2J8e'
  + '3F903K904A915R93aF93bK93cF93dA93eK941F949K94dG94eK950A951F958R960A962F964A978R980A981F982K984A995R9a9A9aaR9b1A9b2R9b3A9b6R9baA9bcF9bdA9beF9b'
  + 'fK9c1F9c5A9c7K9c9A9cbK9cdG9ceA9d7F9d8A9dcR9deA9dfR9e0A9e2F9e4A9f0R9f2A9feF9ffAa01Fa03Ka04Aa3cFa3dAa3eKa41Fa43Aa47Fa49Aa4bFa4eAa51Fa52Aa70Fa7'
  + '2Aa75Fa76Aa81Fa83Ka84Aa95Raa9AaaaRab1Aab2Rab4Aab5RabaAabcFabdAabeKac1Fac6Aac7Fac9KacaAacbKacdGaceAae2Fae4Aaf9RafaFb00Ab01Fb02Kb04Ab15Rb29Ab2'
  + 'aRb31Ab32Rb34Ab35Rb3aAb3cFb3dAb3eFb40Kb41Fb45Ab47Kb49Ab4bKb4dGb4eAb55Fb58Ab5cRb5eAb5fRb60Ab62Fb64Ab71Rb72Ab82Fb83AbbeFbbfKbc0Fbc1Kbc3Abc6Kbc'
  + '9AbcaKbcdFbceAbd7Fbd8Ac00Fc01Kc04Fc05Ac15Rc29Ac2aRc3aAc3cFc3dAc3eFc41Kc45Ac46Fc49Ac4aFc4dGc4eAc55Fc57Ac58Rc5bAc62Fc64Ac81Fc82Kc84AcbcFcbdAcb'
  + 'eKcbfFcc1Kcc2Fcc3Kcc5Acc6Fcc9AccaFcceAcd5Fcd7Ace2Fce4Acf3Kcf4Ad00Fd02Kd04Ad15Rd3bFd3dAd3eFd3fKd41Fd45Ad46Kd49Ad4aKd4dGd4eJd4fAd57Fd58Ad62Fd6'
  + '4Ad81Fd82Kd84AdcaFdcbAdcfFdd0Kdd2Fdd5Add6Fdd7Add8KddfFde0Adf2Kdf4Ae31Fe32Ae33Ke34Fe3bAe47Fe4fAeb1Feb2Aeb3Keb4FebdAec8FecfAf18Ff1aAf35Ff36Af3'
  + '7Ff38Af39Ff3aAf3eKf40Af71Ff7fKf80Ff85Af86Ff88Af8dFf98Af99FfbdAfc6Ffc7A102dF1031K1032F1038A1039F103bK103dF103fA1056K1058F105aA105eF1061A1071F'
  + '1075A1082F1083A1084K1085F1087A108dF108eA109dF109eA1100L1160M11a8N1200A135dF1360A1712F1716A1732F1735A1752F1754A1772F1774A17b4F17b6K17b7F17beK'
  + '17c6F17c7K17c9F17d4A17ddF17deA180bF180eD180fF1810A1885F1887A18a9F18aaA1920F1923K1927F1929K192cA1930K1932F1933K1939F193cA1a17F1a19K1a1bF1a1cA'
  + '1a55K1a56F1a57K1a58F1a5fA1a60F1a61A1a62F1a63A1a65F1a6dK1a73F1a7dA1a7fF1a80A1ab0F1acfA1b00F1b04K1b05A1b34F1b3eK1b42F1b45A1b6bF1b74A1b80F1b82K'
  + '1b83A1ba1K1ba2F1ba6K1ba8F1baeA1be6F1be7K1be8F1beaK1bedF1beeK1befF1bf4A1c24K1c2cF1c34K1c36F1c38A1cd0F1cd3A1cd4F1ce1K1ce2F1ce9A1cedF1ceeA1cf4F'
  + '1cf5A1cf7K1cf8F1cfaA1dc0F1e00A200bD200cE200dH200eD2010A2028D202fA203cQ203dA2049Q204aA2060D2070A20d0F20f1A2122Q2123A2139Q213aA2194Q219aA21a9Q'
  + '21abA231aQ231cA2328Q2329A2388Q2389A23cfQ23d0A23e9Q23f4A23f8Q23fbA24c2Q24c3A25aaQ25acA25b6Q25b7A25c0Q25c1A25fbQ25ffA2600Q2606A2607Q2613A2614Q'
  + '2686A2690Q2706A2708Q2713A2714Q2715A2716Q2717A271dQ271eA2721Q2722A2728Q2729A2733Q2735A2744Q2745A2747Q2748A274cQ274dA274eQ274fA2753Q2756A2757Q'
  + '2758A2763Q2768A2795Q2798A27a1Q27a2A27b0Q27b1A27bfQ27c0A2934Q2936A2b05Q2b08A2b1bQ2b1dA2b50Q2b51A2b55Q2b56A2cefF2cf2A2d7fF2d80A2de0F2e00A302aF'
  + '3030Q3031A303dQ303eA3099F309bA3297Q3298A3299Q329aAa66fFa673Aa674Fa67eAa69eFa6a0Aa6f0Fa6f2Aa802Fa803Aa806Fa807Aa80bFa80cAa823Ka825Fa827Ka828A'
  + 'a82cFa82dAa880Ka882Aa8b4Ka8c4Fa8c6Aa8e0Fa8f2Aa8ffFa900Aa926Fa92eAa947Fa952Ka953Fa954Aa960La97dAa980Fa983Ka984Aa9b3Fa9b4Ka9b6Fa9baKa9bcFa9beK'
  + 'a9c0Fa9c1Aa9e5Fa9e6Aaa29Faa2fKaa31Faa33Kaa35Faa37Aaa43Faa44Aaa4cFaa4dKaa4eAaa7cFaa7dAaab0Faab1Aaab2Faab5Aaab7Faab9AaabeFaac0Aaac1Faac2AaaebK'
  + 'aaecFaaeeKaaf0Aaaf5Kaaf6Faaf7Aabe3Kabe5Fabe6Kabe8Fabe9KabebAabecKabedFabeeAac00Od7a4Ad7b0Md7c7Ad7cbNd7fcAfb1eFfb1fAfe00Ffe10Afe20Ffe30AfeffD'
  + 'ff00Aff9eFffa0Afff0DfffcA101fdF101feA102e0F102e1A10376F1037bA10a01F10a04A10a05F10a07A10a0cF10a10A10a38F10a3bA10a3fF10a40A10ae5F10ae7A10d24F1'
  + '0d28A10d69F10d6eA10eabF10eadA10efcF10f00A10f46F10f51A10f82F10f86A11000K11001F11002K11003A11038F11047A11070F11071A11073F11075A1107fF11082K110'
  + '83A110b0K110b3F110b7K110b9F110bbA110bdJ110beA110c2F110c3A110cdJ110ceA11100F11103A11127F1112cK1112dF11135A11145K11147A11173F11174A11180F11182'
  + 'K11183A111b3K111b6F111bfK111c0F111c1A111c2J111c4A111c9F111cdA111ceK111cfF111d0A1122cK1122fF11232K11234F11238A1123eF1123fA11241F11242A112dfF1'
  + '12e0K112e3F112ebA11300F11302K11304A1133bF1133dA1133eF1133fK11340F11341K11345A11347K11349A1134bK1134dF1134eA11357F11358A11362K11364A11366F113'
  + '6dA11370F11375A113b8F113b9K113bbF113c1A113c2F113c3A113c5F113c6A113c7F113caK113cbA113ccK113ceF113d1J113d2F113d3A113e1F113e3A11435K11438F11440'
  + 'K11442F11445K11446F11447A1145eF1145fA114b0F114b1K114b3F114b9K114baF114bbK114bdF114beK114bfF114c1K114c2F114c4A115afF115b0K115b2F115b6A115b8K1'
  + '15bcF115beK115bfF115c1A115dcF115deA11630K11633F1163bK1163dF1163eK1163fF11641A116abF116acK116adF116aeK116b0F116b8A1171dF1171eK1171fF11720A117'
  + '22F11726K11727F1172cA1182cK1182fF11838K11839F1183bA11930F11931K11936A11937K11939A1193bF1193fJ11940K11941J11942K11943F11944A119d1K119d4F119d8'
  + 'A119daF119dcK119e0F119e1A119e4K119e5A11a01F11a0bA11a33F11a39K11a3aJ11a3bF11a3fA11a47F11a48A11a51F11a57K11a59F11a5cA11a84J11a8aF11a97K11a98F1'
  + '1a9aA11c2fK11c30F11c37A11c38F11c3eK11c3fF11c40A11c92F11ca8A11ca9K11caaF11cb1K11cb2F11cb4K11cb5F11cb7A11d31F11d37A11d3aF11d3bA11d3cF11d3eA11d'
  + '3fF11d46J11d47F11d48A11d8aK11d8fA11d90F11d92A11d93K11d95F11d96K11d97F11d98A11ef3F11ef5K11ef7A11f00F11f02J11f03K11f04A11f34K11f36F11f3bA11f3e'
  + 'K11f40F11f43A11f5aF11f5bA13430D13440F13441A13447F13456A1611eF1612aK1612dF16130A16af0F16af5A16b30F16b37A16d63M16d64A16d67M16d6bA16f4fF16f50A1'
  + '6f51K16f88A16f8fF16f93A16fe4F16fe5A16ff0F16ff2A1bc9dF1bc9fA1bca0D1bca4A1cf00F1cf2eA1cf30F1cf47A1d165F1d16aA1d16dF1d173D1d17bF1d183A1d185F1d1'
  + '8cA1d1aaF1d1aeA1d242F1d245A1da00F1da37A1da3bF1da6dA1da75F1da76A1da84F1da85A1da9bF1daa0A1daa1F1dab0A1e000F1e007A1e008F1e019A1e01bF1e022A1e023'
  + 'F1e025A1e026F1e02bA1e08fF1e090A1e130F1e137A1e2aeF1e2afA1e2ecF1e2f0A1e4ecF1e4f0A1e5eeF1e5f0A1e8d0F1e8d7A1e944F1e94bA1f000Q1f100A1f10dQ1f110A1'
  + 'f12fQ1f130A1f16cQ1f172A1f17eQ1f180A1f18eQ1f18fA1f191Q1f19bA1f1adQ1f1e6I1f200A1f201Q1f210A1f21aQ1f21bA1f22fQ1f230A1f232Q1f23bA1f23cQ1f240A1f2'
  + '49Q1f3fbF1f400Q1f53eA1f546Q1f650A1f680Q1f700A1f774Q1f780A1f7d5Q1f800A1f80cQ1f810A1f848Q1f850A1f85aQ1f860A1f888Q1f890A1f8aeQ1f900A1f90cQ1f93b'
  + 'A1f93cQ1f946A1f947Q1fb00A1fc00Q1fffeAe0000De0020Fe0080De0100Fe01f0De1000A'
  + '';
export const HASHTAG_DATA = ''
  + '0A30B3aA41B5bA61B7bAaaBabAb2Bb4Ab5Bb6Ab9BbbAbcBbfAc0Bd7Ad8Bf7Af8B2c2A2c6B2d2A2e0B2e5A2ecB2edA2eeB2efA370B375A376B378A37aB37eA37fB380A386B387'
  + 'A388B38bA38cB38dA38eB3a2A3a3B3f6A3f7B482A48aB530A531B557A559B55aA560B589A5d0B5ebA5efB5f3A620B64bA660B66aA66eB670A671B6d4A6d5B6d6A6e5B6e7A6ee'
  + 'B6fdA6ffB700A710B711A712B730A74dB7a6A7b1B7b2A7c0B7ebA7f4B7f6A7faB7fbA800B816A81aB81bA824B825A828B829A840B859A860B86bA870B888A889B88fA8a0B8ca'
  + 'A904B93aA93dB93eA950B951A958B962A966B970A971B981A985B98dA98fB991A993B9a9A9aaB9b1A9b2B9b3A9b6B9baA9bdB9beA9ceB9cfA9dcB9deA9dfB9e2A9e6B9f2A9f4'
  + 'B9faA9fcB9fdAa05Ba0bAa0fBa11Aa13Ba29Aa2aBa31Aa32Ba34Aa35Ba37Aa38Ba3aAa59Ba5dAa5eBa5fAa66Ba70Aa72Ba75Aa85Ba8eAa8fBa92Aa93Baa9AaaaBab1Aab2Bab4'
  + 'Aab5BabaAabdBabeAad0Bad1Aae0Bae2Aae6Baf0Aaf9BafaAb05Bb0dAb0fBb11Ab13Bb29Ab2aBb31Ab32Bb34Ab35Bb3aAb3dBb3eAb5cBb5eAb5fBb62Ab66Bb70Ab71Bb78Ab83'
  + 'Bb84Ab85Bb8bAb8eBb91Ab92Bb96Ab99Bb9bAb9cBb9dAb9eBba0Aba3Bba5Aba8BbabAbaeBbbaAbd0Bbd1Abe6Bbf3Ac05Bc0dAc0eBc11Ac12Bc29Ac2aBc3aAc3dBc3eAc58Bc5b'
  + 'Ac5dBc5eAc60Bc62Ac66Bc70Ac78Bc7fAc80Bc81Ac85Bc8dAc8eBc91Ac92Bca9AcaaBcb4Acb5BcbaAcbdBcbeAcddBcdfAce0Bce2Ace6Bcf0Acf1Bcf3Ad04Bd0dAd0eBd11Ad12'
  + 'Bd3bAd3dBd3eAd4eBd4fAd54Bd57Ad58Bd62Ad66Bd79Ad7aBd80Ad85Bd97Ad9aBdb2Adb3BdbcAdbdBdbeAdc0Bdc7Ade6Bdf0Ae01Be31Ae32Be34Ae40Be47Ae50Be5aAe81Be83'
  + 'Ae84Be85Ae86Be8bAe8cBea4Aea5Bea6Aea7Beb1Aeb2Beb4AebdBebeAec0Bec5Aec6Bec7Aed0BedaAedcBee0Af00Bf01Af20Bf34Af40Bf48Af49Bf6dAf88Bf8dA1000B102bA1'
  + '03fB104aA1050B1056A105aB105eA1061B1062A1065B1067A106eB1071A1075B1082A108eB108fA1090B109aA10a0B10c6A10c7B10c8A10cdB10ceA10d0B10fbA10fcB1249A1'
  + '24aB124eA1250B1257A1258B1259A125aB125eA1260B1289A128aB128eA1290B12b1A12b2B12b6A12b8B12bfA12c0B12c1A12c2B12c6A12c8B12d7A12d8B1311A1312B1316A1'
  + '318B135bA1369B137dA1380B1390A13a0B13f6A13f8B13feA1401B166dA166fB1680A1681B169bA16a0B16ebA16eeB16f9A1700B1712A171fB1732A1740B1752A1760B176dA1'
  + '76eB1771A1780B17b4A17d7B17d8A17dcB17ddA17e0B17eaA17f0B17faA1810B181aA1820B1879A1880B1885A1887B18a9A18aaB18abA18b0B18f6A1900B191fA1946B196eA1'
  + '970B1975A1980B19acA19b0B19caA19d0B19dbA1a00B1a17A1a20B1a55A1a80B1a8aA1a90B1a9aA1aa7B1aa8A1b05B1b34A1b45B1b4dA1b50B1b5aA1b83B1ba1A1baeB1be6A1'
  + 'c00B1c24A1c40B1c4aA1c4dB1c7eA1c80B1c8bA1c90B1cbbA1cbdB1cc0A1ce9B1cedA1ceeB1cf4A1cf5B1cf7A1cfaB1cfbA1d00B1dc0A1e00B1f16A1f18B1f1eA1f20B1f46A1'
  + 'f48B1f4eA1f50B1f58A1f59B1f5aA1f5bB1f5cA1f5dB1f5eA1f5fB1f7eA1f80B1fb5A1fb6B1fbdA1fbeB1fbfA1fc2B1fc5A1fc6B1fcdA1fd0B1fd4A1fd6B1fdcA1fe0B1fedA1'
  + 'ff2B1ff5A1ff6B1ffdA2070B2072A2074B207aA207fB208aA2090B209dA2102B2103A2107B2108A210aB2114A2115B2116A2119B211eA2124B2125A2126B2127A2128B2129A2'
  + '12aB212eA212fB213aA213cB2140A2145B214aA214eB214fA2150B218aA2460B249cA24eaB2500A2776B2794A2c00B2ce5A2cebB2cefA2cf2B2cf4A2cfdB2cfeA2d00B2d26A2'
  + 'd27B2d28A2d2dB2d2eA2d30B2d68A2d6fB2d70A2d80B2d97A2da0B2da7A2da8B2dafA2db0B2db7A2db8B2dbfA2dc0B2dc7A2dc8B2dcfA2dd0B2dd7A2dd8B2ddfA2e2fB2e30A3'
  + '005B3008A3021B302aA3031B3036A3038B303dA3041B3097A309dB30a0A30a1B30fbA30fcB3100A3105B3130A3131B318fA3192B3196A31a0B31c0A31f0B3200A3220B322aA3'
  + '248B3250A3251B3260A3280B328aA32b1B32c0A3400B4dc0A4e00Ba48dAa4d0Ba4feAa500Ba60dAa610Ba62cAa640Ba66fAa67fBa69eAa6a0Ba6f0Aa717Ba720Aa722Ba789Aa'
  + '78bBa7ceAa7d0Ba7d2Aa7d3Ba7d4Aa7d5Ba7ddAa7f2Ba802Aa803Ba806Aa807Ba80bAa80cBa823Aa830Ba836Aa840Ba874Aa882Ba8b4Aa8d0Ba8daAa8f2Ba8f8Aa8fbBa8fcAa'
  + '8fdBa8ffAa900Ba926Aa930Ba947Aa960Ba97dAa984Ba9b3Aa9cfBa9daAa9e0Ba9e5Aa9e6Ba9ffAaa00Baa29Aaa40Baa43Aaa44Baa4cAaa50Baa5aAaa60Baa77Aaa7aBaa7bAa'
  + 'a7eBaab0Aaab1Baab2Aaab5Baab7Aaab9BaabeAaac0Baac1Aaac2Baac3AaadbBaadeAaae0BaaebAaaf2Baaf5Aab01Bab07Aab09Bab0fAab11Bab17Aab20Bab27Aab28Bab2fAa'
  + 'b30Bab5bAab5cBab6aAab70Babe3Aabf0BabfaAac00Bd7a4Ad7b0Bd7c7Ad7cbBd7fcAf900Bfa6eAfa70BfadaAfb00Bfb07Afb13Bfb18Afb1dBfb1eAfb1fBfb29Afb2aBfb37Af'
  + 'b38Bfb3dAfb3eBfb3fAfb40Bfb42Afb43Bfb45Afb46Bfbb2Afbd3Bfd3eAfd50Bfd90Afd92Bfdc8Afdf0BfdfcAfe70Bfe75Afe76BfefdAff10Bff1aAff21Bff3bAff41Bff5bAf'
  + 'f66BffbfAffc2Bffc8AffcaBffd0Affd2Bffd8AffdaBffddA10000B1000cA1000dB10027A10028B1003bA1003cB1003eA1003fB1004eA10050B1005eA10080B100fbA10107B1'
  + '0134A10140B10179A1018aB1018cA10280B1029dA102a0B102d1A102e1B102fcA10300B10324A1032dB1034bA10350B10376A10380B1039eA103a0B103c4A103c8B103d0A103'
  + 'd1B103d6A10400B1049eA104a0B104aaA104b0B104d4A104d8B104fcA10500B10528A10530B10564A10570B1057bA1057cB1058bA1058cB10593A10594B10596A10597B105a2'
  + 'A105a3B105b2A105b3B105baA105bbB105bdA105c0B105f4A10600B10737A10740B10756A10760B10768A10780B10786A10787B107b1A107b2B107bbA10800B10806A10808B1'
  + '0809A1080aB10836A10837B10839A1083cB1083dA1083fB10856A10858B10877A10879B1089fA108a7B108b0A108e0B108f3A108f4B108f6A108fbB1091cA10920B1093aA109'
  + '80B109b8A109bcB109d0A109d2B10a01A10a10B10a14A10a15B10a18A10a19B10a36A10a40B10a49A10a60B10a7fA10a80B10aa0A10ac0B10ac8A10ac9B10ae5A10aebB10af0'
  + 'A10b00B10b36A10b40B10b56A10b58B10b73A10b78B10b92A10ba9B10bb0A10c00B10c49A10c80B10cb3A10cc0B10cf3A10cfaB10d24A10d30B10d3aA10d40B10d66A10d6fB1'
  + '0d86A10e60B10e7fA10e80B10eaaA10eb0B10eb2A10ec2B10ec5A10f00B10f28A10f30B10f46A10f51B10f55A10f70B10f82A10fb0B10fccA10fe0B10ff7A11003B11038A110'
  + '52B11070A11071B11073A11075B11076A11083B110b0A110d0B110e9A110f0B110faA11103B11127A11136B11140A11144B11145A11147B11148A11150B11173A11176B11177'
  + 'A11183B111b3A111c1B111c5A111d0B111dbA111dcB111ddA111e1B111f5A11200B11212A11213B1122cA1123fB11241A11280B11287A11288B11289A1128aB1128eA1128fB1'
  + '129eA1129fB112a9A112b0B112dfA112f0B112faA11305B1130dA1130fB11311A11313B11329A1132aB11331A11332B11334A11335B1133aA1133dB1133eA11350B11351A113'
  + '5dB11362A11380B1138aA1138bB1138cA1138eB1138fA11390B113b6A113b7B113b8A113d1B113d2A113d3B113d4A11400B11435A11447B1144bA11450B1145aA1145fB11462'
  + 'A11480B114b0A114c4B114c6A114c7B114c8A114d0B114daA11580B115afA115d8B115dcA11600B11630A11644B11645A11650B1165aA11680B116abA116b8B116b9A116c0B1'
  + '16caA116d0B116e4A11700B1171bA11730B1173cA11740B11747A11800B1182cA118a0B118f3A118ffB11907A11909B1190aA1190cB11914A11915B11917A11918B11930A119'
  + '3fB11940A11941B11942A11950B1195aA119a0B119a8A119aaB119d1A119e1B119e2A119e3B119e4A11a00B11a01A11a0bB11a33A11a3aB11a3bA11a50B11a51A11a5cB11a8a'
  + 'A11a9dB11a9eA11ab0B11af9A11bc0B11be1A11bf0B11bfaA11c00B11c09A11c0aB11c2fA11c40B11c41A11c50B11c6dA11c72B11c90A11d00B11d07A11d08B11d0aA11d0bB1'
  + '1d31A11d46B11d47A11d50B11d5aA11d60B11d66A11d67B11d69A11d6aB11d8aA11d98B11d99A11da0B11daaA11ee0B11ef3A11f02B11f03A11f04B11f11A11f12B11f34A11f'
  + '50B11f5aA11fb0B11fb1A11fc0B11fd5A12000B1239aA12400B1246fA12480B12544A12f90B12ff1A13000B13430A13441B13447A13460B143fbA14400B14647A16100B1611e'
  + 'A16130B1613aA16800B16a39A16a40B16a5fA16a60B16a6aA16a70B16abfA16ac0B16acaA16ad0B16aeeA16b00B16b30A16b40B16b44A16b50B16b5aA16b5bB16b62A16b63B1'
  + '6b78A16b7dB16b90A16d40B16d6dA16d70B16d7aA16e40B16e97A16f00B16f4bA16f50B16f51A16f93B16fa0A16fe0B16fe2A16fe3B16fe4A17000B187f8A18800B18cd6A18c'
  + 'ffB18d09A1aff0B1aff4A1aff5B1affcA1affdB1afffA1b000B1b123A1b132B1b133A1b150B1b153A1b155B1b156A1b164B1b168A1b170B1b2fcA1bc00B1bc6bA1bc70B1bc7d'
  + 'A1bc80B1bc89A1bc90B1bc9aA1ccf0B1ccfaA1d2c0B1d2d4A1d2e0B1d2f4A1d360B1d379A1d400B1d455A1d456B1d49dA1d49eB1d4a0A1d4a2B1d4a3A1d4a5B1d4a7A1d4a9B1'
  + 'd4adA1d4aeB1d4baA1d4bbB1d4bcA1d4bdB1d4c4A1d4c5B1d506A1d507B1d50bA1d50dB1d515A1d516B1d51dA1d51eB1d53aA1d53bB1d53fA1d540B1d545A1d546B1d547A1d5'
  + '4aB1d551A1d552B1d6a6A1d6a8B1d6c1A1d6c2B1d6dbA1d6dcB1d6fbA1d6fcB1d715A1d716B1d735A1d736B1d74fA1d750B1d76fA1d770B1d789A1d78aB1d7a9A1d7aaB1d7c3'
  + 'A1d7c4B1d7ccA1d7ceB1d800A1df00B1df1fA1df25B1df2bA1e030B1e06eA1e100B1e12dA1e137B1e13eA1e140B1e14aA1e14eB1e14fA1e290B1e2aeA1e2c0B1e2ecA1e2f0B1'
  + 'e2faA1e4d0B1e4ecA1e4f0B1e4faA1e5d0B1e5eeA1e5f0B1e5fbA1e7e0B1e7e7A1e7e8B1e7ecA1e7edB1e7efA1e7f0B1e7ffA1e800B1e8c5A1e8c7B1e8d0A1e900B1e944A1e9'
  + '4bB1e94cA1e950B1e95aA1ec71B1ecacA1ecadB1ecb0A1ecb1B1ecb5A1ed01B1ed2eA1ed2fB1ed3eA1ee00B1ee04A1ee05B1ee20A1ee21B1ee23A1ee24B1ee25A1ee27B1ee28'
  + 'A1ee29B1ee33A1ee34B1ee38A1ee39B1ee3aA1ee3bB1ee3cA1ee42B1ee43A1ee47B1ee48A1ee49B1ee4aA1ee4bB1ee4cA1ee4dB1ee50A1ee51B1ee53A1ee54B1ee55A1ee57B1'
  + 'ee58A1ee59B1ee5aA1ee5bB1ee5cA1ee5dB1ee5eA1ee5fB1ee60A1ee61B1ee63A1ee64B1ee65A1ee67B1ee6bA1ee6cB1ee73A1ee74B1ee78A1ee79B1ee7dA1ee7eB1ee7fA1ee'
  + '80B1ee8aA1ee8bB1ee9cA1eea1B1eea4A1eea5B1eeaaA1eeabB1eebcA1f100B1f10dA1fbf0B1fbfaA20000B2a6e0A2a700B2b73aA2b740B2b81eA2b820B2cea2A2ceb0B2ebe1'
  + 'A2ebf0B2ee5eA2f800B2fa1eA30000B3134bA31350B323b0A'
  + '';
export const CUT_DATA = ''
  + '0A41B5bA61B7bAaaBabAb5Bb6AbaBbbAc0Bd7Ad8Bf7Af8B2c2A2c6B2d2A2e0B2e5A2ecB2edA2eeB2efA370B375A376B378A37aB37eA37fB380A386B387A388B38bA38cB38dA3'
  + '8eB3a2A3a3B3f6A3f7B482A48aB530A531B557A559B55aA560B589A5d0B5ebA5efB5f3A620B64bA66eB670A671B6d4A6d5B6d6A6e5B6e7A6eeB6f0A6faB6fdA6ffB700A710B7'
  + '11A712B730A74dB7a6A7b1B7b2A7caB7ebA7f4B7f6A7faB7fbA800B816A81aB81bA824B825A828B829A840B859A860B86bA870B888A889B88fA8a0B8caA904B93aA93dB93eA9'
  + '4dC94eA950B951A958B962A971B981A985B98dA98fB991A993B9a9A9aaB9b1A9b2B9b3A9b6B9baA9bdB9beA9cdC9ceB9cfA9dcB9deA9dfB9e2A9f0B9f2A9fcB9fdAa05Ba0bAa'
  + '0fBa11Aa13Ba29Aa2aBa31Aa32Ba34Aa35Ba37Aa38Ba3aAa4dCa4eAa59Ba5dAa5eBa5fAa72Ba75Aa85Ba8eAa8fBa92Aa93Baa9AaaaBab1Aab2Bab4Aab5BabaAabdBabeAacdCa'
  + 'ceAad0Bad1Aae0Bae2Aaf9BafaAb05Bb0dAb0fBb11Ab13Bb29Ab2aBb31Ab32Bb34Ab35Bb3aAb3dBb3eAb4dCb4eAb5cBb5eAb5fBb62Ab71Bb72Ab83Bb84Ab85Bb8bAb8eBb91Ab'
  + '92Bb96Ab99Bb9bAb9cBb9dAb9eBba0Aba3Bba5Aba8BbabAbaeBbbaAbcdCbceAbd0Bbd1Ac05Bc0dAc0eBc11Ac12Bc29Ac2aBc3aAc3dBc3eAc4dCc4eAc58Bc5bAc5dBc5eAc60Bc'
  + '62Ac80Bc81Ac85Bc8dAc8eBc91Ac92Bca9AcaaBcb4Acb5BcbaAcbdBcbeAccdCcceAcddBcdfAce0Bce2Acf1Bcf3Ad04Bd0dAd0eBd11Ad12Bd3bAd3dBd3eAd4dCd4eBd4fAd54Bd'
  + '57Ad5fBd62Ad7aBd80Ad85Bd97Ad9aBdb2Adb3BdbcAdbdBdbeAdc0Bdc7AdcaCdcbAe01Be31Ae32Be34Ae40Be47Ae81Be83Ae84Be85Ae86Be8bAe8cBea4Aea5Bea6Aea7Beb1Ae'
  + 'b2Beb4AebdBebeAec0Bec5Aec6Bec7AedcBee0Af00Bf01Af40Bf48Af49Bf6dAf88Bf8dA1000B102bA1039C103aA103fB1040A1050B1056A105aB105eA1061B1062A1065B1067'
  + 'A106eB1071A1075B1082A108eB108fA10a0B10c6A10c7B10c8A10cdB10ceA10d0B10fbA10fcB1249A124aB124eA1250B1257A1258B1259A125aB125eA1260B1289A128aB128e'
  + 'A1290B12b1A12b2B12b6A12b8B12bfA12c0B12c1A12c2B12c6A12c8B12d7A12d8B1311A1312B1316A1318B135bA1380B1390A13a0B13f6A13f8B13feA1401B166dA166fB1680'
  + 'A1681B169bA16a0B16ebA16f1B16f9A1700B1712A171fB1732A1740B1752A1760B176dA176eB1771A1780B17b4A17d2C17d3A17d7B17d8A17dcB17ddA1820B1879A1880B1885'
  + 'A1887B18a9A18aaB18abA18b0B18f6A1900B191fA1950B196eA1970B1975A1980B19acA19b0B19caA1a00B1a17A1a20B1a55A1a60C1a61A1aa7B1aa8A1b05B1b34A1b44C1b45'
  + 'B1b4dA1b83B1ba1A1babC1bacA1baeB1bb0A1bbaB1be6A1c00B1c24A1c4dB1c50A1c5aB1c7eA1c80B1c8bA1c90B1cbbA1cbdB1cc0A1ce9B1cedA1ceeB1cf4A1cf5B1cf7A1cfa'
  + 'B1cfbA1d00B1dc0A1e00B1f16A1f18B1f1eA1f20B1f46A1f48B1f4eA1f50B1f58A1f59B1f5aA1f5bB1f5cA1f5dB1f5eA1f5fB1f7eA1f80B1fb5A1fb6B1fbdA1fbeB1fbfA1fc2'
  + 'B1fc5A1fc6B1fcdA1fd0B1fd4A1fd6B1fdcA1fe0B1fedA1ff2B1ff5A1ff6B1ffdA2071B2072A207fB2080A2090B209dA2102B2103A2107B2108A210aB2114A2115B2116A2119'
  + 'B211eA2124B2125A2126B2127A2128B2129A212aB212eA212fB213aA213cB2140A2145B214aA214eB214fA2183B2185A2c00B2ce5A2cebB2cefA2cf2B2cf4A2d00B2d26A2d27'
  + 'B2d28A2d2dB2d2eA2d30B2d68A2d6fB2d70A2d80B2d97A2da0B2da7A2da8B2dafA2db0B2db7A2db8B2dbfA2dc0B2dc7A2dc8B2dcfA2dd0B2dd7A2dd8B2ddfA2e2fB2e30A3005'
  + 'B3007A3031B3036A303bB303dA3041B3097A309dB30a0A30a1B30fbA30fcB3100A3105B3130A3131B318fA31a0B31c0A31f0B3200A3400B4dc0A4e00Ba48dAa4d0Ba4feAa500'
  + 'Ba60dAa610Ba620Aa62aBa62cAa640Ba66fAa67fBa69eAa6a0Ba6e6Aa717Ba720Aa722Ba789Aa78bBa7ceAa7d0Ba7d2Aa7d3Ba7d4Aa7d5Ba7ddAa7f2Ba802Aa803Ba806Ca807'
  + 'Ba80bAa80cBa823Aa840Ba874Aa882Ba8b4Aa8c4Ca8c5Aa8f2Ba8f8Aa8fbBa8fcAa8fdBa8ffAa90aBa926Aa930Ba947Aa960Ba97dAa984Ba9b3Aa9c0Ca9c1Aa9cfBa9d0Aa9e0'
  + 'Ba9e5Aa9e6Ba9f0Aa9faBa9ffAaa00Baa29Aaa40Baa43Aaa44Baa4cAaa60Baa77Aaa7aBaa7bAaa7eBaab0Aaab1Baab2Aaab5Baab7Aaab9BaabeAaac0Baac1Aaac2Baac3Aaadb'
  + 'BaadeAaae0BaaebAaaf2Baaf5Aaaf6Caaf7Aab01Bab07Aab09Bab0fAab11Bab17Aab20Bab27Aab28Bab2fAab30Bab5bAab5cBab6aAab70Babe3Aac00Bd7a4Ad7b0Bd7c7Ad7cb'
  + 'Bd7fcAf900Bfa6eAfa70BfadaAfb00Bfb07Afb13Bfb18Afb1dBfb1eAfb1fBfb29Afb2aBfb37Afb38Bfb3dAfb3eBfb3fAfb40Bfb42Afb43Bfb45Afb46Bfbb2Afbd3Bfd3eAfd50'
  + 'Bfd90Afd92Bfdc8Afdf0BfdfcAfe70Bfe75Afe76BfefdAff21Bff3bAff41Bff5bAff66BffbfAffc2Bffc8AffcaBffd0Affd2Bffd8AffdaBffddA10000B1000cA1000dB10027A'
  + '10028B1003bA1003cB1003eA1003fB1004eA10050B1005eA10080B100fbA10280B1029dA102a0B102d1A10300B10320A1032dB10341A10342B1034aA10350B10376A10380B10'
  + '39eA103a0B103c4A103c8B103d0A10400B1049eA104b0B104d4A104d8B104fcA10500B10528A10530B10564A10570B1057bA1057cB1058bA1058cB10593A10594B10596A1059'
  + '7B105a2A105a3B105b2A105b3B105baA105bbB105bdA105c0B105f4A10600B10737A10740B10756A10760B10768A10780B10786A10787B107b1A107b2B107bbA10800B10806A'
  + '10808B10809A1080aB10836A10837B10839A1083cB1083dA1083fB10856A10860B10877A10880B1089fA108e0B108f3A108f4B108f6A10900B10916A10920B1093aA10980B10'
  + '9b8A109beB109c0A10a00B10a01A10a10B10a14A10a15B10a18A10a19B10a36A10a3fC10a40A10a60B10a7dA10a80B10a9dA10ac0B10ac8A10ac9B10ae5A10b00B10b36A10b4'
  + '0B10b56A10b60B10b73A10b80B10b92A10c00B10c49A10c80B10cb3A10cc0B10cf3A10d00B10d24A10d4aB10d66A10d6fB10d86A10e80B10eaaA10eb0B10eb2A10ec2B10ec5A'
  + '10f00B10f1dA10f27B10f28A10f30B10f46A10f70B10f82A10fb0B10fc5A10fe0B10ff7A11003B11038A11046C11047A11071B11073A11075B11076A11083B110b0A110b9C11'
  + '0baA110d0B110e9A11103B11127A11133C11134A11144B11145A11147B11148A11150B11173A11176B11177A11183B111b3A111c0C111c1B111c5A111daB111dbA111dcB111d'
  + 'dA11200B11212A11213B1122cA11235C11236A1123fB11241A11280B11287A11288B11289A1128aB1128eA1128fB1129eA1129fB112a9A112b0B112dfA11305B1130dA1130fB'
  + '11311A11313B11329A1132aB11331A11332B11334A11335B1133aA1133dB1133eA1134dC1134eA11350B11351A1135dB11362A11380B1138aA1138bB1138cA1138eB1138fA11'
  + '390B113b6A113b7B113b8A113d0C113d1B113d2A113d3B113d4A11400B11435A11442C11443A11447B1144bA1145fB11462A11480B114b0A114c2C114c3A114c4B114c6A114c'
  + '7B114c8A11580B115afA115bfC115c0A115d8B115dcA11600B11630A1163fC11640A11644B11645A11680B116abA116b6C116b7A116b8B116b9A11700B1171bA11740B11747A'
  + '11800B1182cA11839C1183aA118a0B118e0A118ffB11907A11909B1190aA1190cB11914A11915B11917A11918B11930A1193eC1193fB11940A11941B11942A119a0B119a8A11'
  + '9aaB119d1A119e0C119e1B119e2A119e3B119e4A11a00B11a01A11a0bB11a33A11a3aB11a3bA11a47C11a48A11a50B11a51A11a5cB11a8aA11a99C11a9aA11a9dB11a9eA11ab'
  + '0B11af9A11bc0B11be1A11c00B11c09A11c0aB11c2fA11c3fC11c40B11c41A11c72B11c90A11d00B11d07A11d08B11d0aA11d0bB11d31A11d45C11d46B11d47A11d60B11d66A'
  + '11d67B11d69A11d6aB11d8aA11d97C11d98B11d99A11ee0B11ef3A11f02B11f03A11f04B11f11A11f12B11f34A11f42C11f43A11fb0B11fb1A12000B1239aA12480B12544A12'
  + 'f90B12ff1A13000B13430A13441B13447A13460B143fbA14400B14647A16100B1611eA16800B16a39A16a40B16a5fA16a70B16abfA16ad0B16aeeA16b00B16b30A16b40B16b4'
  + '4A16b63B16b78A16b7dB16b90A16d40B16d6dA16e40B16e80A16f00B16f4bA16f50B16f51A16f93B16fa0A16fe0B16fe2A16fe3B16fe4A17000B187f8A18800B18cd6A18cffB'
  + '18d09A1aff0B1aff4A1aff5B1affcA1affdB1afffA1b000B1b123A1b132B1b133A1b150B1b153A1b155B1b156A1b164B1b168A1b170B1b2fcA1bc00B1bc6bA1bc70B1bc7dA1b'
  + 'c80B1bc89A1bc90B1bc9aA1d400B1d455A1d456B1d49dA1d49eB1d4a0A1d4a2B1d4a3A1d4a5B1d4a7A1d4a9B1d4adA1d4aeB1d4baA1d4bbB1d4bcA1d4bdB1d4c4A1d4c5B1d50'
  + '6A1d507B1d50bA1d50dB1d515A1d516B1d51dA1d51eB1d53aA1d53bB1d53fA1d540B1d545A1d546B1d547A1d54aB1d551A1d552B1d6a6A1d6a8B1d6c1A1d6c2B1d6dbA1d6dcB'
  + '1d6fbA1d6fcB1d715A1d716B1d735A1d736B1d74fA1d750B1d76fA1d770B1d789A1d78aB1d7a9A1d7aaB1d7c3A1d7c4B1d7ccA1df00B1df1fA1df25B1df2bA1e030B1e06eA1e'
  + '100B1e12dA1e137B1e13eA1e14eB1e14fA1e290B1e2aeA1e2c0B1e2ecA1e4d0B1e4ecA1e5d0B1e5eeA1e5f0B1e5f1A1e7e0B1e7e7A1e7e8B1e7ecA1e7edB1e7efA1e7f0B1e7f'
  + 'fA1e800B1e8c5A1e900B1e944A1e94bB1e94cA1ee00B1ee04A1ee05B1ee20A1ee21B1ee23A1ee24B1ee25A1ee27B1ee28A1ee29B1ee33A1ee34B1ee38A1ee39B1ee3aA1ee3bB'
  + '1ee3cA1ee42B1ee43A1ee47B1ee48A1ee49B1ee4aA1ee4bB1ee4cA1ee4dB1ee50A1ee51B1ee53A1ee54B1ee55A1ee57B1ee58A1ee59B1ee5aA1ee5bB1ee5cA1ee5dB1ee5eA1e'
  + 'e5fB1ee60A1ee61B1ee63A1ee64B1ee65A1ee67B1ee6bA1ee6cB1ee73A1ee74B1ee78A1ee79B1ee7dA1ee7eB1ee7fA1ee80B1ee8aA1ee8bB1ee9cA1eea1B1eea4A1eea5B1eea'
  + 'aA1eeabB1eebcA20000B2a6e0A2a700B2b73aA2b740B2b81eA2b820B2cea2A2ceb0B2ebe1A2ebf0B2ee5eA2f800B2fa1eA30000B3134bA31350B323b0A'
  + '';
/** The runs of a table: [starts, values]. */
function decodeRuns(data) {
  const starts = [];
  const vals = [];
  let v = 0;
  for (let i = 0; i < data.length; i += 1) {
    const ch = data.charCodeAt(i);
    if (ch >= 65 && ch <= 90) { starts.push(v); vals.push(ch - 65); v = 0; } else v = v * 16 + parseInt(data[i], 16);
  }
  return [Int32Array.from(starts), Int8Array.from(vals)];
}
const GRAPHEME_TABLE = decodeRuns(GRAPHEME_DATA);
const HASHTAG_TABLE = decodeRuns(HASHTAG_DATA);
const CUT_TABLE = decodeRuns(CUT_DATA);
function lookupRun(table, cp) {
  const starts = table[0];
  let lo = 0;
  let hi = starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (starts[mid] <= cp) lo = mid; else hi = mid - 1;
  }
  return table[1][lo];
}
/** The grapheme class of a code point (GC; a lone surrogate is OTHER). */
export function graphemeClass(cp) {
  if (!(cp >= 0 && cp <= 0x10ffff)) return GC.OTHER;
  const k = lookupRun(GRAPHEME_TABLE, cp);
  return k === GC.LV && (cp - 0xac00) % 28 !== 0 ? GC.LVT : k;
}
/** The cut class of a code point: CUT.LETTER, CUT.VIRAMA or CUT.OTHER (a lone surrogate is CUT.OTHER). */
export function cutClass(cp) {
  return cp >= 0 && cp <= 0x10ffff ? lookupRun(CUT_TABLE, cp) : CUT.OTHER;
}
/** A hashtag character: General_Category L or N (the caller adds '_'). */
export function hashtagChar(cp) {
  return cp >= 0 && cp <= 0x10ffff && lookupRun(HASHTAG_TABLE, cp) === 1;
}
// <<< GENERATED unicode-723
